/**
 * The Shop Partner catalogue's taxonomy (ruling 96, 5 November 2026): the small list of
 * categories a partner files each product under, by kind of partner, each saying which details a
 * product in it must give; the fourteen UK allergens; the units; and the photo rules. Every word
 * and figure comes from `catalogue` in config/store.json.
 */
/** Which kind of partner a category is for. */
export const CATEGORY_KINDS = ['grocery', 'restaurant', 'service'];
/** A detail a category may require of every product in it. */
export const CATEGORY_DETAILS = ['unit', 'allergens', 'bestBefore'];
/** Which categories a partner of this kind files products under. */
export function categoryKindFor(kind) {
    return kind === 'food' ? 'restaurant' : kind === 'service' ? 'service' : 'grocery';
}
export function categoriesFor(kind, settings) {
    const wanted = categoryKindFor(kind);
    return settings.categories.filter((category) => category.kind === wanted);
}
export function categoryById(id, settings) {
    if (!id)
        return null;
    return settings.categories.find((category) => category.id === id) ?? null;
}
/**
 * The details a category requires that the product has not given, in plain words, in the order
 * the form asks them. Empty when everything required is there.
 */
export function missingDetails(category, product) {
    const missing = [];
    for (const detail of category.requires) {
        if (detail === 'unit' &&
            !(product.unitQuantity && product.unitQuantity > 0 && product.unitKind)) {
            missing.push({
                detail,
                words: `${category.name} needs the size: how much, and in what (grams, kilograms, millilitres, litres, each, or a pack).`,
            });
        }
        if (detail === 'allergens' && product.allergens === null) {
            missing.push({
                detail,
                words: `${category.name} needs the allergens: tick any of the fourteen it contains, or "None".`,
            });
        }
        if (detail === 'bestBefore' && !product.expiresOn) {
            missing.push({ detail, words: `${category.name} needs a best before or use by date.` });
        }
    }
    return missing;
}
/** The allergens given that are not on the list of fourteen, so a typo is never shown as fact. */
export function unknownAllergens(given, settings) {
    const known = new Set(settings.allergens.map((name) => name.toLowerCase()));
    return given.filter((name) => !known.has(name.trim().toLowerCase()));
}
/** "Contains milk, eggs and tree nuts", or "No listed allergens", for the shop page. */
export function allergenWords(allergens) {
    if (allergens === null)
        return null;
    if (allergens.length === 0)
        return 'No listed allergens';
    const lower = allergens.map((name) => name.toLowerCase());
    if (lower.length === 1)
        return `Contains ${lower[0]}`;
    return `Contains ${lower.slice(0, -1).join(', ')} and ${lower[lower.length - 1]}`;
}
/**
 * The plain words shown to a partner about a photo, from what was measured against the rules:
 * null when it is fine, otherwise one sentence saying what to do, and whether it is refused.
 */
export function photoFeedback(measured, rules) {
    const longSide = Math.max(measured.width, measured.height);
    if (longSide < rules.hardFloorLongSide) {
        return {
            words: `That photo is too small to show clearly (${longSide} pixels on its long side). Please take it again, closer, with the camera itself.`,
            refused: true,
        };
    }
    if (measured.brightness < rules.darkBelow) {
        return { words: 'That photo is too dark. Try it near a window.', refused: false };
    }
    if (measured.brightness > rules.brightAbove) {
        return { words: 'That photo is too bright. Move it out of direct light.', refused: false };
    }
    if (measured.sharpness < rules.blurBelow) {
        return { words: 'That photo is blurry. Hold the phone still, then try again.', refused: false };
    }
    if (longSide < rules.minimumLongSide) {
        return {
            words: `That photo is small (${longSide} pixels on its long side). It will do, but a bigger one looks better.`,
            refused: false,
        };
    }
    return null;
}
/** The photo rules the big delivery apps give, in plain words, for the add form (ruling 96). */
export const PHOTO_RULES_WORDS = [
    'One product in the photo, in the middle, filling most of it.',
    'Show what is actually sold: a four-pack shows four, a loaf shows the loaf.',
    'Plain background, good light, nothing blurry. Near a window is best.',
    'No words, prices or other products laid over it.',
];
/**
 * The columns of the CSV a Pro shop uploads (ruling 96), in order. Allergens are written as the
 * list spells them, separated by semicolons, or "none".
 */
export const BULK_COLUMNS = [
    'name',
    'price',
    'category',
    'unit quantity',
    'unit',
    'allergens',
    'barcode',
    'description',
];
//# sourceMappingURL=catalogue.js.map